import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Typography } from 'antd';
import {
  DollarOutlined, TeamOutlined, ApiOutlined, RiseOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { agentAPI } from '../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;

const COLORS = ['#5470c6','#91cc75','#fac858','#ee6666','#73c0de','#3ba272','#fc8452','#9a60b4','#ea7ccc','#48c9b0'];

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await agentAPI.getConsumptionStats({
        start_date: dayjs().startOf('month').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
      <Spin size="large" tip="加载数据中..." />
    </div>
  );

  const summary = stats?.summary || {};
  const byModel = stats?.by_model || [];
  const daily = stats?.daily || [];

  // 环形图
  const pieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie',
      radius: ['55%', '80%'],
      center: ['50%', '45%'],
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 3 },
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 16, fontWeight: 'bold' } },
      data: byModel.map((m, i) => ({
        name: m.display_name || m.model,
        value: parseFloat((m.total_cost || 0).toFixed(2)),
        itemStyle: { color: COLORS[i % COLORS.length] },
      })),
    }],
  };

  // 趋势图
  const lineOption = {
    tooltip: { trigger: 'axis', formatter: (params) => {
      let h = `<b>${params[0].axisValue}</b><br/>`;
      params.forEach(p => { h += `${p.marker} ${p.seriesName}: ¥${p.value.toFixed(2)}<br/>`; });
      return h;
    }},
    legend: { bottom: 0, textStyle: { fontSize: 11 }, type: 'scroll' },
    grid: { left: 60, right: 25, top: 40, bottom: 45, containLabel: true },
    xAxis: {
      type: 'category',
      data: daily.map(d => dayjs(d.date).format('MM-DD')),
      axisLabel: { fontSize: 11 },
      boundaryGap: true,
    },
    yAxis: {
      type: 'value',
      name: '消费金额（元）',
      nameGap: 16,
      nameTextStyle: { fontSize: 11, color: '#888', align: 'left' },
      axisLabel: { fontSize: 11, formatter: (v) => `¥${v}` },
    },
    series: [...new Set(daily.flatMap(d => Object.keys(d.models || {})))].map((name, i) => ({
      name,
      type: 'line',
      smooth: true,
      symbol: 'none',
      stack: 'Total',
      areaStyle: { opacity: 0.55 },
      lineStyle: { width: 1 },
      emphasis: { focus: 'series' },
      data: daily.map(d => parseFloat(((d.models || {})[name] || 0).toFixed(2))),
      color: COLORS[i % COLORS.length],
    })),
  };

  const kpiCards = [
    { title: '本月消费总额', value: summary.total_cost || 0, prefix: <DollarOutlined />, suffix: '元', precision: 2, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { title: '总请求数', value: summary.total_requests || 0, prefix: <ApiOutlined />, precision: 0, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { title: '活跃用户', value: summary.active_users || 0, prefix: <TeamOutlined />, precision: 0, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { title: '使用模型数', value: byModel.length, prefix: <RiseOutlined />, precision: 0, gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  ];

  return (
    <div>
      <Row gutter={[20, 20]}>
        {kpiCards.map((item, idx) => (
          <Col xs={24} sm={12} lg={6} key={idx}>
            <Card bordered={false} className="kpi-card" style={{
              background: item.gradient, borderRadius: 14, overflow: 'hidden', position: 'relative',
            }}>
              <div style={{ position: 'absolute', top: -15, right: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 500 }}>{item.title}</Text>}
                value={item.value}
                precision={item.precision}
                prefix={React.cloneElement(item.prefix, { style: { fontSize: 20 } })}
                suffix={<Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>{item.suffix}</Text>}
                valueStyle={{ color: '#fff', fontSize: 30, fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
        <Col xs={24} lg={14}>
          <Card bordered={false} style={{ borderRadius: 14 }} title={<Text strong>消费趋势（本月）</Text>}>
            {daily.length > 0 ? (
              <ReactECharts option={lineOption} style={{ height: 380 }} />
            ) : (
              <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                暂无消费数据
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card bordered={false} style={{ borderRadius: 14 }} title={<Text strong>模型消费占比</Text>}>
            {byModel.length > 0 ? (
              <ReactECharts option={pieOption} style={{ height: 380 }} />
            ) : (
              <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                暂无消费数据
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
